/**
 * Client-side price sorting functionality for Shopify products
 * Handles price sorting before products appear in the DOM
 * Works with existing load-more/infinite loading functionalities
 * Also manages sort button and modal state updates
 */

class PriceSorter {
  constructor() {
    this.isEnabled = false;
    this.currentSortOrder = null;
    this.originalOnSubmitForm = null;
    this.originalRenderProductGridContainer = null;
    this.isProcessing = false;
    this.mutationObserver = null;
    this.debounceTimer = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupSorting());
    } else {
      this.setupSorting();
    }
  }

  setupSorting() {
    // Check if page loaded with price sorting parameters
    this.checkInitialSortState();

    // Hook into the facets form submission
    this.interceptFacetSubmission();

    // Hook into product grid rendering
    this.interceptProductGridRendering();

    // Listen for pagination events
    this.setupPaginationListener();

    // Listen for browser navigation events
    this.setupNavigationListener();
  }

  setupNavigationListener() {
    // Handle browser back/forward navigation
    window.addEventListener('popstate', (event) => {
      // Re-check sort state when navigating
      this.checkInitialSortState();
    });
  }

  checkInitialSortState() {
    const urlParams = new URLSearchParams(window.location.search);
    const sortOrder = urlParams.get('sort_by');

    if (sortOrder === 'price-ascending' || sortOrder === 'price-descending') {
      this.isEnabled = true;
      this.currentSortOrder = sortOrder;

      // Update dropdown state to reflect current sort
      this.updateSortDropdownState(sortOrder);

      // Apply sorting to existing products
      setTimeout(() => {
        this.sortProductsInDOM();
      }, 100);
    }
  }

  interceptFacetSubmission() {
    // Wait for facet form to be available
    const checkForFacetForm = () => {
      const facetForm = document.querySelector('facet-filters-form');
      if (facetForm && facetForm.onSubmitForm) {
        // Store original method
        this.originalOnSubmitForm = facetForm.onSubmitForm.bind(facetForm);

        // Override with our custom method
        facetForm.onSubmitForm = (searchParams, event) => {
          this.handleFormSubmission(searchParams, event, facetForm);
        };
      } else {
        // Retry after a short delay
        setTimeout(checkForFacetForm, 100);
      }
    };

    checkForFacetForm();
  }

  interceptProductGridRendering() {
    // Hook into the static renderProductGridContainer method
    const checkForFacetFiltersForm = () => {
      if (
        window.FacetFiltersForm &&
        window.FacetFiltersForm.renderProductGridContainer
      ) {
        this.originalRenderProductGridContainer =
          window.FacetFiltersForm.renderProductGridContainer;

        window.FacetFiltersForm.renderProductGridContainer = (html) => {
          // Call original method first
          this.originalRenderProductGridContainer(html);

          // Apply sorting if enabled
          if (this.isEnabled && this.currentSortOrder) {
            // Small delay to ensure DOM is fully updated
            setTimeout(() => this.sortProductsInDOM(), 10);
          }
        };
      } else {
        // Retry after a short delay
        setTimeout(checkForFacetFiltersForm, 100);
      }
    };

    checkForFacetFiltersForm();
  }

  setupPaginationListener() {
    // Listen for pagination events to sort newly loaded products
    document.addEventListener('update:infinite:product-grid', () => {
      if (this.isEnabled && this.currentSortOrder && !this.isProcessing) {
        this.debounceSort();
      }
    });

    // Also listen for DOM mutations to catch dynamically added products
    this.mutationObserver = new MutationObserver((mutations) => {
      if (this.isEnabled && this.currentSortOrder && !this.isProcessing) {
        this.handleDOMChanges(mutations);
      }
    });

    // Observe the product grid container
    const productGrid = document.querySelector('#ProductGridContainer');
    if (productGrid) {
      this.mutationObserver.observe(productGrid, {
        childList: true,
        subtree: true,
      });
    }
  }

  handleDOMChanges(mutations) {
    // Check if changes were caused by new products being added
    const hasNewProducts = mutations.some(
      (mutation) =>
        mutation.addedNodes.length > 0 &&
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.classList?.contains('card-wrapper') ||
              node.querySelector?.('.card-wrapper')),
        ),
    );

    if (hasNewProducts) {
      this.debounceSort();
    }
  }

  debounceSort() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.sortProductsInDOM();
    }, 100);
  }

  handleFormSubmission(searchParams, event, facetForm) {
    // Check if this is a price sort request
    const sortOrder = this.extractSortOrder(searchParams);

    if (sortOrder === 'price-ascending' || sortOrder === 'price-descending') {
      this.isEnabled = true;
      this.currentSortOrder = sortOrder;

      // Remove sort_by from search params to prevent server-side sorting
      const modifiedParams = this.removeSortFromParams(searchParams);

      // Call original method with modified params
      this.originalOnSubmitForm(modifiedParams, event);

      // Update URL with the original sort parameter after the request
      setTimeout(() => {
        this.updateURLWithSort(searchParams);
        this.updateSortDropdownState(sortOrder);
      }, 100);
    } else {
      this.isEnabled = false;
      this.currentSortOrder = null;

      // Call original method with original params
      this.originalOnSubmitForm(searchParams, event);
    }
  }

  extractSortOrder(searchParams) {
    const params = new URLSearchParams(searchParams);
    return params.get('sort_by');
  }

  removeSortFromParams(searchParams) {
    const params = new URLSearchParams(searchParams);
    params.delete('sort_by');
    return params.toString();
  }

  updateURLWithSort(originalSearchParams) {
    // Update the URL to include the sort parameter
    const currentUrl = new URL(window.location);
    const params = new URLSearchParams(originalSearchParams);

    // Clear existing search params and add the new ones
    currentUrl.search = '';
    params.forEach((value, key) => {
      currentUrl.searchParams.set(key, value);
    });

    // Update the URL without triggering a page reload
    history.pushState(
      {searchParams: originalSearchParams},
      '',
      currentUrl.toString(),
    );
  }

  updateSortDropdownState(sortOrder) {
    // Update radio buttons state and their labels
    const sortRadios = document.querySelectorAll('input[name="sort_by"]');
    let activeSortText = '';

    sortRadios.forEach((radio) => {
      const isActive = radio.value === sortOrder;
      radio.checked = isActive;

      // Update label styling
      const label = radio.closest('label');
      if (label) {
        if (isActive) {
          label.classList.add('mdl:leo-bg-brand-grey-900', 'mdl:leo-text-white');
          label.classList.remove('mdl:leo-text-black');
          activeSortText = label.textContent.trim();
        } else {
          label.classList.remove('mdl:leo-bg-brand-grey-900', 'mdl:leo-text-white');
          label.classList.add('mdl:leo-text-black');
        }
      }
    });

    // Update the sort button text
    const sortBtn = document.querySelector('.sort-btn');
    if (sortBtn && activeSortText) {
      // Find the text node (not the icon)
      const textNodes = Array.from(sortBtn.childNodes).filter(
        (node) =>
          node.nodeType === Node.TEXT_NODE ||
          (node.nodeType === Node.ELEMENT_NODE && !node.querySelector('svg')),
      );

      if (textNodes.length > 0) {
        // Update the first text node or create one
        if (textNodes[0].nodeType === Node.TEXT_NODE) {
          textNodes[0].textContent = activeSortText;
        } else {
          textNodes[0].textContent = activeSortText;
        }
      } else {
        // If no text node exists, create one
        const textNode = document.createTextNode(activeSortText);
        sortBtn.insertBefore(textNode, sortBtn.firstChild);
      }
    }

    // Update select dropdown if it exists
    const sortSelect = document.querySelector('select[name="sort_by"]');
    if (sortSelect) {
      sortSelect.value = sortOrder;
    }
  }

  sortProductsInDOM() {
    // Temporarily disconnect observer to prevent infinite loops
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    this.isProcessing = true;

    const productGrid =
      document.querySelector('#product-grid') ||
      document.querySelector('#ProductGridContainer .collection') ||
      document.querySelector('#ProductGridContainer product-grid') ||
      document.querySelector('#ProductGridContainer');
    if (!productGrid) {
      this.reconnectObserver();
      return;
    }

    // Get product cards using the specific selector pattern
    const products = Array.from(productGrid.children).filter((child) => {
      return (
        child.classList.contains('card-wrapper') ||
        child.querySelector('.card-wrapper')
      );
    });

    if (products.length === 0) {
      this.reconnectObserver();
      return;
    }

    // Sort products by price
    const sortedProducts = this.sortProductsByPrice(
      products,
      this.currentSortOrder,
    );

    // Reorder DOM elements
    this.reorderProductsInDOM(productGrid, sortedProducts);

    // Reconnect observer after sorting is complete
    this.reconnectObserver();
  }

  reconnectObserver() {
    this.isProcessing = false;

    if (this.mutationObserver) {
      const productGrid = document.querySelector('#ProductGridContainer');
      if (productGrid) {
        this.mutationObserver.observe(productGrid, {
          childList: true,
          subtree: true,
        });
      }
    }
  }

  sortProductsByPrice(products, sortOrder) {
    return products.sort((a, b) => {
      const priceA = this.extractPrice(a);
      const priceB = this.extractPrice(b);

      if (priceA === null && priceB === null) return 0;
      if (priceA === null) return 1;
      if (priceB === null) return -1;

      if (sortOrder === 'price-ascending') {
        return priceA - priceB;
      } else {
        return priceB - priceA;
      }
    });
  }

  extractPrice(productElement) {
    // Use the specific price selector for this theme
    const priceElement = productElement.querySelector(
      '.price-item--sale > .money',
    );

    if (priceElement) {
      const priceText = priceElement.textContent || priceElement.innerText;
      const price = this.parsePrice(priceText);
      if (price !== null) {
        return price;
      }
    }

    // Fallback to regular price if sale price not found
    const regularPriceElement = productElement.querySelector(
      '.price-item--regular > .money',
    );
    if (regularPriceElement) {
      const priceText =
        regularPriceElement.textContent || regularPriceElement.innerText;
      const price = this.parsePrice(priceText);
      if (price !== null) {
        return price;
      }
    }

    // Final fallback: try data attributes
    const productCard = productElement.querySelector('[data-product-price]');
    if (productCard) {
      const dataPrice = productCard.getAttribute('data-product-price');
      if (dataPrice) {
        return this.parsePrice(dataPrice);
      }
    }

    return null;
  }

  parsePrice(priceText) {
    if (!priceText) return null;

    // Remove all non-numeric characters except decimal points and commas
    let cleanPrice = priceText.replace(/[^0-9.,]/g, '');

    // Handle different decimal separators
    // If there are multiple commas or periods, assume the last one is decimal
    if (cleanPrice.includes(',') && cleanPrice.includes('.')) {
      // Determine which is the decimal separator based on position
      const lastComma = cleanPrice.lastIndexOf(',');
      const lastPeriod = cleanPrice.lastIndexOf('.');

      if (lastPeriod > lastComma) {
        // Period is decimal separator
        cleanPrice = cleanPrice
          .replace(/,/g, '')
          .replace(/\./g, (match, offset) =>
            offset === lastPeriod ? '.' : '',
          );
      } else {
        // Comma is decimal separator
        cleanPrice = cleanPrice
          .replace(/\./g, '')
          .replace(/,/g, (match, offset) => (offset === lastComma ? '.' : ''));
      }
    } else if (cleanPrice.includes(',')) {
      // Only commas - check if it's thousands separator or decimal
      const parts = cleanPrice.split(',');
      if (parts.length === 2 && parts[1].length <= 2) {
        // Likely decimal separator
        cleanPrice = cleanPrice.replace(',', '.');
      } else {
        // Likely thousands separator
        cleanPrice = cleanPrice.replace(/,/g, '');
      }
    }

    const price = parseFloat(cleanPrice);
    return isNaN(price) ? null : price;
  }

  reorderProductsInDOM(container, sortedProducts) {
    // Add processing class for visual feedback
    container.classList.add('processing');

    // Create a document fragment for efficient DOM manipulation
    const fragment = document.createDocumentFragment();

    // Append sorted products to fragment
    sortedProducts.forEach((product) => {
      fragment.appendChild(product);
    });

    // Get non-product children to preserve them
    const nonProductChildren = Array.from(container.children).filter(
      (child) => {
        return !(
          child.classList.contains('card-wrapper') ||
          child.querySelector('.card-wrapper')
        );
      },
    );

    // Clear container efficiently
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // Add back non-product elements first
    nonProductChildren.forEach((child) => container.appendChild(child));

    // Add sorted products
    container.appendChild(fragment);

    // Remove processing class
    container.classList.remove('processing');
  }
}

// Universal sort button and modal update function (works for all sort types)
window.updateSortButtonText = function() {
  const urlParams = new URLSearchParams(window.location.search);
  const currentSort = urlParams.get('sort_by');
  
  // Only proceed if there's an active sort
  if (!currentSort) {
    return;
  }
  
  // Update radio buttons state in sort modal
  const sortRadios = document.querySelectorAll('input[name="sort_by"]');
  let activeSortText = '';
  
  sortRadios.forEach((radio) => {
    const isActive = radio.value === currentSort;
    radio.checked = isActive;
    
    // Update visual styling for sort modal labels
    const label = radio.closest('label');
    if (label) {
      if (isActive) {
        // Add active styling
        label.classList.add('mdl:leo-bg-brand-grey-900', 'mdl:leo-text-white');
        activeSortText = label.textContent.trim();
      } else {
        // Remove active styling
        label.classList.remove('mdl:leo-bg-brand-grey-900', 'mdl:leo-text-white');
      }
    }
  });
  
  // Update the sort button text
  const sortBtn = document.querySelector('.sort-btn');
  if (sortBtn && activeSortText) {
    // Find the text node (not the icon)
    const textNodes = Array.from(sortBtn.childNodes).filter(
      (node) =>
        node.nodeType === Node.TEXT_NODE ||
        (node.nodeType === Node.ELEMENT_NODE && !node.querySelector('svg')),
    );
    
    if (textNodes.length > 0) {
      // Update the first text node
      if (textNodes[0].nodeType === Node.TEXT_NODE) {
        textNodes[0].textContent = activeSortText;
      } else {
        textNodes[0].textContent = activeSortText;
      }
    } else {
      // If no text node exists, create one
      const textNode = document.createTextNode(activeSortText);
      sortBtn.insertBefore(textNode, sortBtn.firstChild);
    }
  }
  
  // Update select dropdown if it exists
  const sortSelect = document.querySelector('select[name="sort_by"]');
  if (sortSelect) {
    sortSelect.value = currentSort;
  }
};

// Initialize price sorter when script loads
const priceSorter = new PriceSorter();

// Export for potential external use
window.priceSorter = priceSorter;

// Initialize sort button text on page load
document.addEventListener('DOMContentLoaded', () => {
  window.updateSortButtonText();
});

// Also update on popstate (browser navigation)
window.addEventListener('popstate', () => {
  window.updateSortButtonText();
});
